import React from 'react';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  CarOutlined,
  EnvironmentOutlined,
  TagOutlined,
  TrademarkOutlined,
} from '@ant-design/icons';
import { COLLAPSE_MS, COLORS, ROW_HEIGHT } from '../constants';
import CarStatus from '../../shared/CarStatus';
import { CarLike, RowEntry } from '../types';
import { GroupBy } from '../utils/grouping';
import {
  ColumnCtx,
  TIMELINE_COLUMN_DEFS,
  TimelineColumnKey,
} from '../utils/timelineColumns';

// CarsTable's plate colour (theme `colors.blue`, via
// shared/TableLayout/getRenderWithPlaceholder.tsx) — keeps the timeline plate
// visually consistent with the cars table and signals it's a link.
const PLATE_LINK_COLOR = '#0077be';

// Group-row icon per active dimension (antd outline icons, recoloured #314158).
const GROUP_ICONS: Record<
  GroupBy,
  React.ComponentType<{ style?: React.CSSProperties }>
> = {
  none: EnvironmentOutlined, // not shown when grouping is off; safe default
  area: EnvironmentOutlined,
  fleet: ApartmentOutlined,
  status: TagOutlined,
  category: AppstoreOutlined,
  brand: TrademarkOutlined,
  model: CarOutlined,
};

const GroupIcon: React.FC<{ groupBy: GroupBy }> = ({ groupBy }) => {
  const Icon = GROUP_ICONS[groupBy] ?? EnvironmentOutlined;
  return <Icon style={{ flex: '0 0 auto', fontSize: 16, color: '#314158' }} />;
};

// Car-count pill for a group row — black background, right-aligned just before the
// chevron.
const CountBadge: React.FC<{ count: number }> = ({ count }) => (
  <span
    style={{
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 18,
      height: 18,
      padding: '0 6px',
      background: '#000000',
      color: '#FFFFFF',
      borderRadius: 9,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1,
    }}
  >
    {count}
  </span>
);

// Chevron points DOWN when expanded, rotates to point RIGHT (-90deg) when
// collapsed. Rotation transitions smoothly.
const Chevron: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <span
    aria-hidden
    style={{
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
      transition: `transform ${COLLAPSE_MS}ms ease`,
    }}
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 9l6 6 6-6"
        stroke="#314158"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

type Props = {
  rows: RowEntry[];
  collapsedKeys: Set<string>;
  onToggleGroup: (key: string) => void;
  // Click a row → open that vehicle's drawer (same as CarTable row click).
  onPlateClick: (car: CarLike) => void;
  // Active grouping dimension — selects the group-row icon.
  groupBy: GroupBy;
  // Total sticky-left width + the optional columns to render right of the plate.
  leftWidth: number;
  extraColumns: TimelineColumnKey[];
  // Effective width per left column ('plate' + each extra key) → grid tracks.
  columnWidths: Record<string, number>;
  columnCtx: ColumnCtx;
};

// Sticky-left grid: [plate | optional columns]. Each car is one cell per column;
// each group is one full-width header cell. Collapsed car rows stay mounted at
// height 0 and animate (height + opacity) in lockstep with the body rows.
const PlateColumn: React.FC<Props> = ({
  rows,
  collapsedKeys,
  onToggleGroup,
  onPlateClick,
  groupBy,
  leftWidth,
  extraColumns,
  columnWidths,
  columnCtx,
}) => {
  // [plate, …extra columns] grid tracks from the (resizable) per-column widths.
  const gridTemplateColumns = [
    columnWidths.plate,
    ...extraColumns.map((key) => columnWidths[key]),
  ]
    .map((w) => `${w}px`)
    .join(' ');
  const cellBase: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
    minWidth: 0,
    transition: `height ${COLLAPSE_MS}ms ease`,
  };

  return (
    <div
      className="vt-plate-col"
      style={{
        position: 'sticky',
        left: 0,
        zIndex: 2,
        width: leftWidth,
        flex: '0 0 auto',
        display: 'grid',
        gridTemplateColumns,
        alignContent: 'start',
        background: COLORS.plateColumnBg,
        // No right border — shadow alone separates the sticky column.
        boxShadow: COLORS.plateColumnShadow,
        boxSizing: 'border-box',
      }}
    >
      {rows.map((row, i) => {
        if (row.kind === 'group') {
          const collapsed = collapsedKeys.has(row.key);
          return (
            <div
              key={`group-${row.key}-${i}`}
              role="button"
              tabIndex={0}
              onClick={() => onToggleGroup(row.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleGroup(row.key);
                }
              }}
              style={{
                ...cellBase,
                height: ROW_HEIGHT,
                gridColumn: '1 / -1',
                gap: 8,
                padding: '0 12px',
                background: COLORS.groupRowBg,
                borderTop: `1px solid ${COLORS.plateColumnBorder}`,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              {groupBy === 'status' ? (
                // Group-by-status: render the status itself as a CarStatus badge
                // (row.key is the raw status, e.g. BLOCKED) instead of icon + text.
                <>
                  <CarStatus
                    status={row.key}
                    style={{
                      fontSize: 11,
                      padding: '1px 8px',
                      borderRadius: 10,
                    }}
                  />
                  <span style={{ flex: '1 1 auto' }} />
                </>
              ) : (
                <>
                  <GroupIcon groupBy={groupBy} />
                  {/* Group name — flexes and ellipsises; chevron keeps its fixed slot.
                      `display:block` is REQUIRED: a global rule makes bare spans
                      `display:flex`, and text-overflow:ellipsis is a no-op on flex
                      boxes (it would hard-clip with no "…"). Inline style wins. */}
                  <span
                    style={{
                      display: 'block',
                      flex: '1 1 auto',
                      minWidth: 0,
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#314158',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.name}
                  </span>
                </>
              )}
              <CountBadge count={row.count} />
              <Chevron collapsed={collapsed} />
            </div>
          );
        }

        const { car } = row;
        const hidden = !!row.groupKey && collapsedKeys.has(row.groupKey);
        const rowHeight = hidden ? 0 : ROW_HEIGHT;
        const rowOpacity = hidden ? 0 : 1;
        const open = () => onPlateClick(car);
        // Shared per-cell style (height/opacity/borderTop animate on collapse).
        const carCell: React.CSSProperties = {
          ...cellBase,
          height: rowHeight,
          opacity: rowOpacity,
          padding: '0 16px',
          cursor: 'pointer',
          borderTop: hidden
            ? '0 solid transparent'
            : `1px solid ${COLORS.plateColumnBorder}`,
        };
        return (
          <React.Fragment key={`car-${car.id}`}>
            {/* Plate cell — the clickable link to the vehicle drawer. */}
            <div
              role="button"
              tabIndex={hidden ? -1 : 0}
              onClick={open}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  open();
                }
              }}
              style={carCell}
            >
              <span
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  // CarsTable plate colour — signals the plate is a link.
                  color: PLATE_LINK_COLOR,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {car.plate || '—'}
              </span>
            </div>

            {/* Optional columns — values reuse CarsTable derivations. Whole row
                is clickable (opens the same drawer). */}
            {extraColumns.map((key) => (
              <div key={key} onClick={open} style={carCell}>
                {/* block + overflow:hidden + ellipsis so long text values show "…".
                    (inline-flex would disable text-overflow.) Component values like
                    the status badge keep their own size and just clip if too wide. */}
                <span
                  style={{
                    fontSize: 13,
                    color: COLORS.plateText,
                    display: 'block',
                    width: '100%',
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {TIMELINE_COLUMN_DEFS[key].getValue(car, columnCtx)}
                </span>
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default PlateColumn;
